import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, ArrowLeft, ArrowRight, User, Mail, Building, MessageSquare, Check, Loader } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1=pick date, 2=pick time, 3=details, 4=confirmed
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, slotsRes] = await Promise.all([
          axios.get(`${API}/bookings/settings`),
          axios.get(`${API}/bookings/available`),
        ]);
        setSettings(settingsRes.data);
        setSlots(slotsRes.data.slots || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group slots by date
  const dateMap = {};
  slots.forEach(s => {
    if (!dateMap[s.date]) dateMap[s.date] = [];
    dateMap[s.date].push(s);
  });
  const availableDates = Object.keys(dateMap).sort();

  const handleBook = async () => {
    if (!name.trim() || !email.trim()) { toast.error('Name and email are required'); return; }
    setBooking(true);
    try {
      await axios.post(`${API}/bookings/book`, {
        slot_id: selectedSlot.id,
        name, email, company, message
      });
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to book. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatDateShort = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return { day: d.toLocaleDateString('en-US', { weekday: 'short' }), date: d.getDate(), month: d.toLocaleDateString('en-US', { month: 'short' }) };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--main-bg)] flex items-center justify-center">
        <Loader size={24} className="animate-spin text-[var(--text-muted)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--main-bg)] text-[var(--text-primary)]">
      {/* Header */}
      <div className="border-b-2 border-[var(--border-default)] bg-[var(--surface)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            data-testid="booking-back-btn"
            className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        {/* Title */}
        <div className="mb-10">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-none"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            {settings?.meeting_title || 'BOOK A MEETING'}
          </h1>
          <p className="text-base text-[var(--text-secondary)] mt-3 max-w-lg">
            {settings?.meeting_description || 'Pick a time that works for you.'}
          </p>
          {settings?.duration_minutes && (
            <div className="flex items-center gap-2 mt-3 text-xs text-[var(--text-muted)]">
              <Clock size={12} /> {settings.duration_minutes} minutes
              {settings.location && <><span>&middot;</span> {settings.location}</>}
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1 flex-1 ${step >= s ? 'bg-[var(--secondary)]' : 'bg-[var(--surface-alt)]'} transition-colors`} />
          ))}
        </div>

        {/* Step 1: Pick Date */}
        {step === 1 && (
          <div>
            <h2 className="text-sm font-bold uppercase tracking-widest text-[var(--text-muted)] mb-4">Select a Date</h2>
            {availableDates.length === 0 ? (
              <div className="border-2 border-[var(--border-default)] p-8 text-center bg-[var(--surface)]">
                <Calendar size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
                <p className="text-[var(--text-secondary)]">No available time slots right now.</p>
                <p className="text-xs text-[var(--text-muted)] mt-1">Check back soon or contact us directly.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {availableDates.map(date => {
                  const f = formatDateShort(date);
                  const slotCount = dateMap[date].length;
                  return (
                    <button
                      key={date}
                      data-testid={`date-${date}`}
                      onClick={() => { setSelectedDate(date); setStep(2); }}
                      className="border-2 border-[var(--border-default)] bg-[var(--surface)] p-4 text-center hover:border-[var(--secondary)] hover:bg-[var(--secondary)]/5 transition-all"
                    >
                      <p className="text-[10px] font-bold uppercase text-[var(--text-muted)]">{f.day}</p>
                      <p className="text-2xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{f.date}</p>
                      <p className="text-xs text-[var(--text-muted)]">{f.month}</p>
                      <p className="text-[10px] mt-1 text-[var(--secondary)] font-bold">{slotCount} slot{slotCount > 1 ? 's' : ''}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Pick Time */}
        {step === 2 && selectedDate && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold uppercase tracking-widest text-[var(--text-muted)]">
                {formatDate(selectedDate)}
              </h2>
              <button onClick={() => { setStep(1); setSelectedSlot(null); }} className="text-xs text-[var(--secondary)] font-bold">
                Change Date
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {dateMap[selectedDate].map(slot => (
                <button
                  key={slot.id}
                  data-testid={`slot-${slot.id}`}
                  onClick={() => { setSelectedSlot(slot); setStep(3); }}
                  className="border-2 border-[var(--border-default)] bg-[var(--surface)] p-4 text-center hover:border-[var(--secondary)] hover:bg-[var(--secondary)]/5 transition-all"
                >
                  <Clock size={16} className="mx-auto mb-2 text-[var(--text-muted)]" />
                  <p className="text-lg font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {slot.start_time}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">{slot.start_time} - {slot.end_time}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Your Details */}
        {step === 3 && selectedSlot && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold uppercase tracking-widest text-[var(--text-muted)]">Your Details</h2>
              <button onClick={() => setStep(2)} className="text-xs text-[var(--secondary)] font-bold">
                Change Time
              </button>
            </div>

            {/* Selected slot summary */}
            <div className="border-2 border-[var(--secondary)] bg-[var(--secondary)]/5 p-4 mb-6 flex items-center gap-3">
              <Calendar size={18} className="text-[var(--secondary)]" />
              <div>
                <p className="text-sm font-bold">{formatDate(selectedDate)}</p>
                <p className="text-xs text-[var(--text-muted)]">{selectedSlot.start_time} - {selectedSlot.end_time}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)] block mb-1">Name *</label>
                <div className="flex items-center border-2 border-[var(--border-default)] bg-[var(--surface)]">
                  <User size={16} className="ml-3 text-[var(--text-muted)]" />
                  <input
                    data-testid="booking-name"
                    value={name} onChange={e => setName(e.target.value)}
                    placeholder="Your full name"
                    className="flex-1 px-3 py-2.5 text-sm bg-transparent text-[var(--text-primary)] outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)] block mb-1">Email *</label>
                <div className="flex items-center border-2 border-[var(--border-default)] bg-[var(--surface)]">
                  <Mail size={16} className="ml-3 text-[var(--text-muted)]" />
                  <input
                    data-testid="booking-email"
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="flex-1 px-3 py-2.5 text-sm bg-transparent text-[var(--text-primary)] outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)] block mb-1">Company</label>
                <div className="flex items-center border-2 border-[var(--border-default)] bg-[var(--surface)]">
                  <Building size={16} className="ml-3 text-[var(--text-muted)]" />
                  <input
                    data-testid="booking-company"
                    value={company} onChange={e => setCompany(e.target.value)}
                    placeholder="Your company (optional)"
                    className="flex-1 px-3 py-2.5 text-sm bg-transparent text-[var(--text-primary)] outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)] block mb-1">Message</label>
                <div className="flex items-start border-2 border-[var(--border-default)] bg-[var(--surface)]">
                  <MessageSquare size={16} className="ml-3 mt-3 text-[var(--text-muted)]" />
                  <textarea
                    data-testid="booking-message"
                    value={message} onChange={e => setMessage(e.target.value)}
                    placeholder="What would you like to discuss? (optional)"
                    rows={3}
                    className="flex-1 px-3 py-2.5 text-sm bg-transparent text-[var(--text-primary)] outline-none resize-none"
                  />
                </div>
              </div>

              <button
                data-testid="confirm-booking-btn"
                onClick={handleBook}
                disabled={booking}
                className="w-full font-bold text-sm px-6 py-3.5 bg-[var(--secondary)] text-white border-2 border-[var(--border-default)] transition-all hover:translate-y-[-1px] active:translate-y-[1px] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {booking ? <><Loader size={14} className="animate-spin" /> Booking...</> : 'Confirm Meeting'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Confirmed */}
        {step === 4 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-6 bg-[var(--secondary)] flex items-center justify-center">
              <Check size={32} className="text-white" />
            </div>
            <h2
              className="text-2xl sm:text-3xl font-black tracking-tight mb-3"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              YOU'RE BOOKED!
            </h2>
            <p className="text-[var(--text-secondary)] mb-2">
              {formatDate(selectedDate)} at {selectedSlot?.start_time} - {selectedSlot?.end_time}
            </p>
            <p className="text-sm text-[var(--text-muted)] mb-8">
              A confirmation email has been sent to <strong>{email}</strong>
            </p>
            <button
              onClick={() => navigate('/')}
              className="text-sm font-bold text-[var(--secondary)] underline"
            >
              Back to VAULT
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
